import { AsyncPipe, CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, Inject, NgZone, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { TranslocoCurrencyPipe, TranslocoDatePipe, TranslocoDecimalPipe, TranslocoPercentPipe } from '@jsverse/transloco-locale';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { MultiSelectModule } from 'primeng/multiselect';
import { RippleModule } from 'primeng/ripple';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { BehaviorSubject, debounceTime, filter, Subject, takeUntil } from 'rxjs';
import { Bike } from '../../interfaces/bike';
import { getDefaultTask, Task, TaskFilter, TaskHistory, TaskStatus } from '../../interfaces/task';
import { TaskType } from '../../interfaces/taskType';
import { Column } from '../../interfaces/table';
import { DurationPipe } from '../../pipes/duration.pipe';
import { ConfirmDialogsService } from '../../services/confirm-dialogs.service';
import { NotificationsService } from '../../services/notifications.service';
import { getPathKey } from '../../utilities/util';
import { TaskAction } from '../../interfaces/taskAction';
import { ActivatedRoute, Router } from '@angular/router';
import { Automation, AutomationFilter, AutomationTrigger, getDefaultAutomation } from '../../interfaces/automation';
import { BikeModel } from '../../interfaces/bikeModel';
import { DatePickerModule } from 'primeng/datepicker';
import { SkeletonModule } from 'primeng/skeleton';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { AuthStateService } from '../../services/authstate.service';
import { LongPressDirective } from '../../directives/long-press.directive';



type MAIN_I = Task
type FILTER_I = TaskFilter

type MAIN_I2 = Automation
type FILTER_I2 = AutomationFilter

@Component({
  selector: 'app-admin-task-view',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    RippleModule,
    InputTextModule,
    TranslocoDirective,
    DialogModule,
    FloatLabelModule,
    InputNumberModule,
    MenuModule,
    MultiSelectModule,
    SelectModule,
    TextareaModule,
    DatePickerModule,
    TranslocoDatePipe,
    SkeletonModule,
    InputGroupModule,
    InputGroupAddonModule,
    LongPressDirective
  ],
  providers: [TranslocoDatePipe, TranslocoCurrencyPipe, TranslocoDecimalPipe, TranslocoPercentPipe, DurationPipe],
  templateUrl: './admin-task-view.component.html',
  styleUrl: './admin-task-view.component.scss'
})
export class AdminTaskViewComponent implements OnDestroy, AfterViewInit {
  //^ --------------------------------------------------------------------------------------
  //^ ---------------------- CONFIGURAZIONE BASE -------------------------------------------
  //^ --------------------------------------------------------------------------------------

  //? TRADUZIONI
  TITLE = "tasks"
  TITLE2 = "automations"
  SEARCH_PLACEHOLDER = "searchTasks"
  SEARCH_PLACEHOLDER2 = "searchAutomations"
  ADD_BUTTON_LABEL = "newFeminine"
  ADD_BUTTON_LABEL2 = "newFeminine"
  NEW_RECORD_TITLE = "newTask"
  NEW_RECORD_TITLE2 = "newAutomation"
  EDIT_RECORD_TITLE = "editTaskWithCode"
  EDIT_RECORD_TITLE2 = "editAutomationWithCode"
  FILTERS_TITLE = "filterTasks"
  FILTERS_TITLE2 = "filterAutomations"
  DATA_SAVED_MESSAGE = "taskSaved"
  DATA_SAVED_MESSAGE2 = "automationSaved"
  DELETE_CONFIRMATION_MESSAGE = "deleteTaskConfirm"
  DELETE_CONFIRMATION_MESSAGE2 = "deleteAutomationConfirm"

  //? DATI
  KEY_NAME = 'taskId'
  KEY_NAME2 = 'automationId'
  DEFAULT_DATA_FN = getDefaultTask
  DEFAULT_DATA_FN2 = getDefaultAutomation
  GET_DATA_ENDPOINT = "getTasks"
  GET_DATA_ENDPOINT2 = "getAutomations"
  GET_RECORD_ENDPOINT = "getTask"
  GET_RECORD_ENDPOINT2 = "getAutomation"
  GET_FILTERS_ENDPOINT = "getTasksFilters"
  GET_FILTERS_ENDPOINT2 = "getAutomationsFilters"
  CREATE_RECORD_ENDPOINT = "createTask"
  CREATE_RECORD_ENDPOINT2 = "createAutomation"
  EDIT_RECORD_ENDPOINT = "editTask"
  EDIT_RECORD_ENDPOINT2 = "editAutomation"
  DELETE_DATA_ENDPOINT = "deleteTasks"
  DELETE_DATA_ENDPOINT2 = "deleteAutomations"

  //! configurare columns, dataFilters e _batchMenuStructure 

  //^ --------------------------------------------------------------------------------------
  //^ ---------------------- INIZIALIZZAZIONI ----------------------------------------------
  //^ --------------------------------------------------------------------------------------

  //? DATI E TABELLE
  columns: Column[] = [
    { field: 'id', header: 'id', visible: false },
    { field: 'code', header: 'code', visible: true },
    { field: 'bike', header: 'bike', visible: true, nestedField: 'bikeDetails.code' },
    { field: 'action', header: 'action', visible: true, nestedField: 'actionDetails.code' },
    {
      field: 'type', header: 'type', visible: true, nestedField: 'typeDetails.description', pipe: "translate", wrapper: Tag, wrapperConfig: {
        rounded: true,
        value: "{{value}}",
        icon: "{{typeDetails.icon}}",
        styleClass: "table-tag opacity",
        style: { "--bg-color": "{{typeDetails.color}}" },
      },
    },
    {
      field: 'status', header: 'status', visible: true, nestedField: 'statusDetails.description', pipe: "translate", wrapper: Tag, wrapperConfig: {
        rounded: true,
        value: "{{value}}",
        icon: "{{statusDetails.icon}}",
        styleClass: "table-tag opacity",
        style: { "--bg-color": "{{statusDetails.color}}" },
      },
    },
    { field: 'creationDate', header: 'creation', visible: true, pipe: ["date", { dateStyle: "short", timeStyle: "medium" }] },

  ]
  columns2: Column[] = [
    { field: 'id', header: 'id', visible: false },
    { field: 'code', header: 'code', visible: true },
    { field: 'action', header: 'action', visible: true, nestedField: 'actionDetails.code' },
    {
      field: 'trigger', header: 'trigger', visible: true, nestedField: "triggerTypeDetails.description", pipe: ["translate", { value: "{{triggerValue}}" }], wrapper: Tag, wrapperConfig: {
        rounded: true,
        value: "{{value}}",
        icon: "{{triggerTypeDetails.icon}}",
        styleClass: "table-tag opacity",
        style: { "--bg-color": "{{triggerTypeDetails.color}}" },
      },
    },
    { field: "description", header: "description", visible: true, pipe: ['custom', (v: any) => { return v.length >= 50 ? v.substring(0, 50) + '...' : v }] },
    { field: "triggerValue", header: "triggerValue", visible: false, filterable: true },
  ]

  historyFields: Column[] = [
    { field: 'code', header: 'code' },
    { field: 'notes', header: 'notes' },
    { field: 'action', header: 'action', nestedField: 'code' },
    { field: 'type', header: 'type', nestedField: 'description', pipe: "translate" },
    { field: 'status', header: 'status', nestedField: 'description', pipe: "translate" },
    { field: 'bike', header: 'bike', nestedField: 'code' },
  ]
  data: MAIN_I[] = []
  data2: MAIN_I2[] = []

  _types: TaskType[] = []
  _statuses: TaskStatus[] = []
  _automationsTriggers: AutomationTrigger[] = []
  bikes: Bike[] = []
  types: TaskType[] = []
  statuses: TaskStatus[] = []
  actions: TaskAction[] = []
  automationsTriggers: AutomationTrigger[] = []
  bikesModels: BikeModel[] = []

  get dataFilterFields() {
    return this.columns.filter(column => column.visible || column.filterable).map(column => column.field);
  }

  get dataFilterFields2() {
    return this.columns2.filter(column => column.visible || column.filterable).map(column => column.field);
  }


  selectedRows: any[] = []
  selectedRows2: any[] = []

  dataLoading$ = new BehaviorSubject<boolean>(false)
  dataLoading2$ = new BehaviorSubject<boolean>(false)

  dataLoadingUI$ = this.dataLoading$.pipe(
    debounceTime(200)
  );
  dataLoadingUI2$ = this.dataLoading2$.pipe(
    debounceTime(200)
  );

  _dataTrackBy: (index: number, item: any) => any = (index: number, item: any) => {
    return item.KEY_NAME
  };

  _dataTrackBy2: (index: number, item: any) => any = (index: number, item: any) => {
    return item.KEY_NAME2
  };

  //? VIEW
  formDialogVisible: boolean = false;
  formDialogVisible2: boolean = false;
  filtersDialogVisible: boolean = false;
  filtersDialogVisible2: boolean = false;
  formDialogData: MAIN_I = this.DEFAULT_DATA_FN();
  formDialogData2: MAIN_I2 = this.DEFAULT_DATA_FN2();

  dataFilters: FILTER_I = {
    options: {
      status: [],
      type: [],
    },
    cache: {
      status: [],
      type: [],
    },
    values: {},
    applied: {}
  };
  dataFilters2: FILTER_I2 = {
    options: {
      triggerType: [],
    },
    cache: {
      triggerType: [],
    },
    values: {},
    applied: {}
  };
  get appliedFiltersCount() {
    let cnt = Object.values(this.dataFilters.applied).filter(value => value !== undefined && value !== null).length
    return cnt == 0 ? undefined : String(cnt)
  }
  get appliedFiltersCount2() {
    let cnt = Object.values(this.dataFilters2.applied).filter(value => value !== undefined && value !== null).length
    return cnt == 0 ? undefined : String(cnt)
  }

  get isNewRecordDialog() {
    return this.formDialogData[(this.KEY_NAME as keyof MAIN_I)] === ''
  }
  get isNewRecordDialog2() {
    return this.formDialogData2[(this.KEY_NAME2 as keyof MAIN_I2)] === ''
  }

  _batchMenuStructure: MenuItem[] = [
    {
      label: 'batchOperations',
      items: [
        {
          label: 'deleteSelected',
          icon: 'pi pi-trash',
          command: () => {
            this.deleteRecords()
          }
        }
      ]
    }
  ]
  _batchMenuStructure2: MenuItem[] = [
    {
      label: 'batchOperations',
      items: [
        {
          label: 'deleteSelected',
          icon: 'pi pi-trash',
          command: () => {
            this.deleteRecords2()
          }
        }
      ]
    }
  ]

  batchMenu: MenuItem[] = []
  batchMenu2: MenuItem[] = []

  activeEditSection: 'data' | 'history' | 'actionDetails' = 'data';

  //? TABS
  activeTab: string = '';

  //? ALTRO
  destroy$ = new Subject<void>();

  get curAuthLevel() {
    return this.auth.loginData.user.authLevel
  }

  get isStationManager() {
    return this.curAuthLevel == "4"
  }

  get canCreateTask() {
    return true
  }

  get canEditTask() {
    return true
  }

  get canDeleteTask() {
    return !this.isStationManager
  }

  get canViewAutomations() {
    return !this.isStationManager
  }

  //^ --------------------------------------------------------------------------------------
  //^ ---------------------- LIFE CYCLE - EVENTI -------------------------------------------
  //^ --------------------------------------------------------------------------------------

  constructor(
    private zone: NgZone,
    private http: HttpClient,
    private confirmDialogs: ConfirmDialogsService,
    private notifications: NotificationsService,
    private transloco: TranslocoService,
    private translocoDate: TranslocoDatePipe,
    private translocoCurrency: TranslocoCurrencyPipe,
    private translocoDecimal: TranslocoDecimalPipe,
    private translocoPercent: TranslocoPercentPipe,
    private durationPipe: DurationPipe,
    private router: Router,
    private route: ActivatedRoute,
    private auth: AuthStateService,
    @Inject('ENVIRONMENT') private ENV: any
  ) {
    transloco.events$.pipe(
      takeUntil(this.destroy$),
      filter((event) => event.type === "translationLoadSuccess")
    ).subscribe((e) => {
      this.batchMenu = this.translateObjectValues(this._batchMenuStructure, 'label')
    });

    this.route.params.subscribe(params => {
      const tab = params['tab'];
      this.setActiveTab(tab, false);
      if (tab == "manage") {
        this.refreshData()
      } else {
        if(this.canViewAutomations){
          this.refreshData2()
        }
      }
    });
  }


  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.transloco.langChanges$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.batchMenu = this.translateObjectValues(this._batchMenuStructure, 'label')
      this.batchMenu2 = this.translateObjectValues(this._batchMenuStructure2, 'label')
    });
  }

  //^ --------------------------------------------------------------------------------------
  //^ ---------------------- ALTRO ---------------------------------------------------------
  //^ --------------------------------------------------------------------------------------

  //? DATI E TABELLE
  refreshData() {
    this.dataLoading$.next(true)
    this.http.post(this.ENV.apiUrl + this.GET_DATA_ENDPOINT, {
      filters: this.dataFilters.values
    }).subscribe({
      next: (resp: any) => {
        this.dataFilters.applied = structuredClone(this.dataFilters.values)
        this.data = resp.data;
        this.selectedRows = []
        this.dataLoading$.next(false)
        this.http.post(this.ENV.apiUrl + this.GET_FILTERS_ENDPOINT, {}).subscribe({
          next: (resp: any) => {
            this.dataFilters.options = resp?.data;
          }
        })
      },
      error: () => {
        this.dataLoading$.next(false)
      }
    })

    this.http.post(this.ENV.apiUrl + "getBikes", {}).subscribe({
      next: (resp: any) => {
        this.bikes = resp.data;
      }
    })

    this.http.post(this.ENV.apiUrl + "getTasksTypes", {}).subscribe({
      next: (resp: any) => {
        this._types = resp.data;
        this.types = this.translateObjectValues(this._types, 'description')
      }
    })

    this.http.post(this.ENV.apiUrl + "getTasksStatuses", {}).subscribe({
      next: (resp: any) => {
        this._statuses = resp.data;
        this.statuses = this.translateObjectValues(this._statuses, 'description')
      }
    })

    this.http.post(this.ENV.apiUrl + "getTasksActions", {}).subscribe({
      next: (resp: any) => {
        this.actions = resp.data;
      }
    })
  }

  refreshData2() {
    this.dataLoading2$.next(true)
    this.http.post(this.ENV.apiUrl + this.GET_DATA_ENDPOINT2, {
      filters: this.dataFilters2.values
    }).subscribe({
      next: (resp: any) => {
        this.dataFilters2.applied = structuredClone(this.dataFilters2.values)
        this.data2 = resp.data;
        this.selectedRows2 = []
        this.dataLoading2$.next(false)
        this.http.post(this.ENV.apiUrl + this.GET_FILTERS_ENDPOINT2, {}).subscribe({
          next: (resp: any) => {
            this.dataFilters2.options = resp?.data;
          }
        })
      },
      error: () => {
        this.dataLoading2$.next(false)
      }
    })

    this.http.post(this.ENV.apiUrl + "getTasksActions", {}).subscribe({
      next: (resp: any) => {
        this.actions = resp.data;
      }
    })

    this.http.post(this.ENV.apiUrl + "getBikeModels", {}).subscribe({
      next: (resp: any) => {
        this.bikesModels = resp.data;
      }
    })

    this.http.post(this.ENV.apiUrl + "getAutomationsTriggers", {}).subscribe({
      next: (resp: any) => {
        this._automationsTriggers = resp.data;
        this.automationsTriggers = this.translateObjectValues(this._automationsTriggers, 'description', { value: "N" })
      }
    })
  }

  openFilters() {
    this.filtersDialogVisible = true;
  }

  openFilters2() {
    this.filtersDialogVisible2 = true;
  }

  toggleFilters() {
    this.filtersDialogVisible = !this.filtersDialogVisible;
  }

  toggleFilters2() {
    this.filtersDialogVisible2 = !this.filtersDialogVisible2;
  }

  hideFilters() {
    this.filtersDialogVisible = false;
  }

  hideFilters2() {
    this.filtersDialogVisible2 = false;
  }

  openFormDialog(record?: MAIN_I) {
    if (record) {
      this.dataLoading$.next(true)
      this.http.post(this.ENV.apiUrl + this.GET_RECORD_ENDPOINT, { ...record, excludeRefs: "*" }).subscribe({
        next: (resp: any) => {
          this.selectedRows = []
          this.formDialogData = resp.data
          this.formDialogVisible = true;
          this.dataLoading$.next(false)
        },
        error: () => {
          this.dataLoading$.next(false)
        }
      })
    } else {
      this.formDialogData = this.DEFAULT_DATA_FN()
      this.formDialogVisible = true
    }
  }

  openFormDialog2(record?: MAIN_I2) {
    if (record) {
      this.dataLoading2$.next(true)
      this.http.post(this.ENV.apiUrl + this.GET_RECORD_ENDPOINT2, { ...record, excludeRefs: "*" }).subscribe({
        next: (resp: any) => {
          this.selectedRows2 = []
          this.formDialogData2 = resp.data
          this.formDialogVisible2 = true;
          this.dataLoading2$.next(false);
        },
        error: () => {
          this.dataLoading2$.next(false);
        }
      })
    } else {
      this.formDialogData2 = this.DEFAULT_DATA_FN2()
      this.formDialogVisible2 = true
    }
  }

  hideFormDialog() {
    this.formDialogVisible = false;
    this.formDialogData = this.DEFAULT_DATA_FN();
  }

  hideFormDialog2() {
    this.formDialogVisible2 = false;
    this.formDialogData2 = this.DEFAULT_DATA_FN2();
  }

  saveRecord() {
    this.dataLoading$.next(true);
    this.http.post(this.ENV.apiUrl + (this.isNewRecordDialog ? this.CREATE_RECORD_ENDPOINT : this.EDIT_RECORD_ENDPOINT), this.cleanDtoToSend(this.formDialogData)).subscribe({
      next: (resp: any) => {
        if (resp.code == 201) {
          this.notifications.showNotification({
            severity: "info",
            title: "noModifications",
            addToHistory: false
          })
        } else {
          this.refreshData()
          this.notifications.showNotification({
            severity: "success",
            title: "operationComplete",
            detail: this.DATA_SAVED_MESSAGE,
            detailParams: {
              count: 1
            },
            translateDetailsParams: false,
            addToHistory: false
          })
        }
        this.hideFormDialog()
        this.dataLoading$.next(false);
      },
      error: (err) => {
        this.dataLoading$.next(false);
      }
    })
  }

  updateTaskStatus(task: Task, status: string) {
    task.status = status
    this.dataLoading$.next(true);
    this.http.post(this.ENV.apiUrl + this.EDIT_RECORD_ENDPOINT, this.cleanDtoToSend(task)).subscribe({
      next: (resp: any) => {
        if (resp.code == 201) {
          this.notifications.showNotification({
            severity: "info",
            title: "noModifications",
            addToHistory: false
          })
        } else {
          this.refreshData()
          this.notifications.showNotification({
            severity: "success",
            title: "operationComplete",
            translateDetailsParams: false,
            addToHistory: false
          })
        }
        this.hideFormDialog()
        this.dataLoading$.next(false);
      },
      error: (err) => {
        this.dataLoading$.next(false);
      }
    })
  }

  saveRecord2() {
    this.dataLoading2$.next(true);
    this.http.post(this.ENV.apiUrl + (this.isNewRecordDialog2 ? this.CREATE_RECORD_ENDPOINT2 : this.EDIT_RECORD_ENDPOINT2), this.cleanDtoToSend(this.formDialogData2)).subscribe({
      next: (resp: any) => {
        if (resp.code == 201) {
          this.notifications.showNotification({
            severity: "info",
            title: "noModifications",
            addToHistory: false
          })
        } else {
          this.refreshData2()
          this.notifications.showNotification({
            severity: "success",
            title: "operationComplete",
            detail: this.DATA_SAVED_MESSAGE2,
            detailParams: {
              count: 1
            },
            translateDetailsParams: false,
            addToHistory: false
          })
        }
        this.hideFormDialog2()
        this.dataLoading2$.next(false);
      },
      error: (err) => {
        this.dataLoading2$.next(false);
      }
    })
  }

  deleteRecord() {
    this.confirmDialogs.showDeleteConfirmAuto({
      endpoint: this.DELETE_DATA_ENDPOINT,
      selection: [this.formDialogData],
      message: this.DELETE_CONFIRMATION_MESSAGE,
      idKey: this.KEY_NAME,
      onAcceptClick: () => {
        this.dataLoading$.next(true);
      },
      successCallback: () => {
        this.refreshData()
        this.hideFormDialog()
      },
      completeCallback: () => {
        this.dataLoading$.next(false);
      }
    })
  }

  deleteRecord2() {
    this.confirmDialogs.showDeleteConfirmAuto({
      endpoint: this.DELETE_DATA_ENDPOINT2,
      selection: [this.formDialogData2],
      message: this.DELETE_CONFIRMATION_MESSAGE2,
      idKey: this.KEY_NAME2,
      onAcceptClick: () => {
        this.dataLoading2$.next(true);
      },
      successCallback: () => {
        this.refreshData2()
        this.hideFormDialog2()
      },
      completeCallback: () => {
        this.dataLoading2$.next(false);
      }
    })
  }

  deleteRecords() {
    this.confirmDialogs.showDeleteConfirmAuto({
      endpoint: this.DELETE_DATA_ENDPOINT,
      selection: this.selectedRows,
      message: this.DELETE_CONFIRMATION_MESSAGE,
      idKey: this.KEY_NAME,
      onAcceptClick: () => {
        this.dataLoading$.next(true);
      },
      successCallback: () => {
        this.refreshData()
        this.selectedRows = []
      },
      completeCallback: () => {
        this.dataLoading$.next(false);
      }
    })
  }

  deleteRecords2() {
    this.confirmDialogs.showDeleteConfirmAuto({
      endpoint: this.DELETE_DATA_ENDPOINT2,
      selection: this.selectedRows2,
      message: this.DELETE_CONFIRMATION_MESSAGE2,
      idKey: this.KEY_NAME2,
      onAcceptClick: () => {
        this.dataLoading2$.next(true);
      },
      successCallback: () => {
        this.refreshData2()
        this.selectedRows2 = []
      },
      completeCallback: () => {
        this.dataLoading2$.next(false);
      }
    })
  }

  //? TABS
  setActiveTab(tab: string, fromUserAction: boolean = true) {
    if(tab == "automations" && !this.canViewAutomations){
      this.setActiveTab("manage", true)
      return
    }
    if (tab != this.activeTab) {
      this.hideFilters();
      this.hideFilters2()

      // Only navigate if change was triggered by user action
      if (fromUserAction) {
        this.router.navigate(['/admin', 'tasks', tab]);
      }
    }

    this.activeTab = tab;
  }

  //? ALTRO
  translateObjectValues<T>(obj: T, keyToTranslate: string, params?: any): T {
    if (Array.isArray(obj)) {
      return obj.map(item => this.translateObjectValues(item, keyToTranslate, params)) as T;
    }

    if (obj && typeof obj === 'object') {
      const result = { ...obj };
      for (const [key, value] of Object.entries(result)) {
        if (key === keyToTranslate && typeof value === 'string') {
          (result as { [key: string]: any })[key] = this.transloco.translate(value, params);
        } else if (typeof value === 'object') {
          (result as { [key: string]: any })[key] = this.translateObjectValues(value, keyToTranslate, params);
        }
      }
      return result as T;
    }
    return obj;
  }

  onFiltersDialogHide() {
    this.dataFilters.values = structuredClone(this.dataFilters.applied)
  }

  onFiltersDialogHide2() {
    this.dataFilters2.values = structuredClone(this.dataFilters2.applied)
  }

  getCellData(col: Column, data: any) {
    let pipe = col.pipe

    let value = getPathKey(data, col.nestedField ?? col.field, col.defaultValue ?? '<span class="null-chip">null</span>')

    if (pipe) {
      if (!Array.isArray(pipe)) {
        pipe = [pipe, {}]
      }
      let pipeconfig = pipe[1]
      pipeconfig = this.replaceObjectValues(pipeconfig, getPathKey(data, col.nestedField ?? col.field, col.defaultValue), data, value)
      switch (pipe[0]) {
        case "date":
          value = this.translocoDate.transform(value, pipeconfig, pipeconfig.locale);
          break

        case "currency":
          value = this.translocoCurrency.transform(value, pipeconfig.display, pipeconfig.numberFormatOptions, pipeconfig.currencyCode, pipeconfig.locale);
          break

        case "percent":
          value = this.translocoPercent.transform(value, pipeconfig, pipeconfig.locale);
          break

        case 'decimal':
          value = this.translocoDecimal.transform(value, pipeconfig, pipeconfig.locale);
          break

        case 'translate':
          value = this.transloco.translate(String(value), pipeconfig)
          break

        case 'duration':
          value = this.durationPipe.transform(value, pipeconfig);
          break

        case "custom":
          value = pipe[1](value)
          break

        default:
          return value;
      }
    }
    return value;
  }

  cleanDtoToSend(dto: any) {
    let res = structuredClone(dto)
    for (const key in res) {
      if (res[key] === undefined || res[key] === null || res[key] === "") {
        delete res[key]
      }
    }
    return res
  }

  parseWrapperInputs(col: Column, value: [any, any]) {
    let inputs = col.wrapperConfig;
    if (inputs) {
      inputs = structuredClone(inputs);
      const rowData = value[0];
      const currentColumnValue = value[1];
      const currentColumnValueOriginal = getPathKey(rowData, col.nestedField ?? col.field, col.defaultValue ?? '<span class="null-chip">null</span>');
      return this.replaceObjectValues(inputs, currentColumnValue, rowData, currentColumnValueOriginal)
    } else {
      return {};
    }
  }

  replaceObjectValues(obj: { [key: string]: any }, currentColumnValue: any, rowData: any, currentColumnValueOriginal: any) {
    if (typeof obj === "object" && obj !== null) {
      let objectCopy = structuredClone(obj);

      for (let key in objectCopy) {
        if (typeof objectCopy[key] === 'string') {
          // Replace {{columnField}} with the corresponding value from rowData
          objectCopy[key] = objectCopy[key].replace(/{{([\w.]+)}}/g, (match: any, path: any) => {
            if (path === 'value') {
              return currentColumnValue;
            }
            return getPathKey(rowData, path, match);
          });
        } else if (typeof objectCopy[key] === 'object') {
          // iterate thru key-val
          for (let subKey in objectCopy[key]) {
            if (typeof objectCopy[key][subKey] === 'string') {
              // Replace {{columnField}} with the corresponding value from rowData
              objectCopy[key][subKey] = objectCopy[key][subKey].replace(/{{([\w.]+)}}/g, (match: any, path: any) => {
                let res = currentColumnValue
                if (path !== 'value') {
                  res = getPathKey(rowData, path, match);
                }
                if (subKey == "--state-color" && res == "base") {
                  res = "var(--p-surface-100)"
                }
                if (subKey == "--state-color-text") {
                  res = res == "base" ? "var(--p-text-color)" : "var(--p-primary-color-text)"
                }
                return res
              });
            }
          }
        }

        const match = key.match(/^(.+)_if_(.+)_(.+)$/);
        if (match) {
          const baseKey = match[1];
          const compareType = match[2];
          const compareValue = match[3];

          let valueToCompare;
          if (compareType === 'val') {
            valueToCompare = currentColumnValueOriginal;
          } else {
            valueToCompare = getPathKey(rowData, compareType, undefined);
          }

          if (String(valueToCompare) === compareValue) {
            if (typeof objectCopy[key] == "string") {
              objectCopy[baseKey] = objectCopy[key].startsWith("%+t%") ?
                this.transloco.translate(objectCopy[key].replace("%+t%", "")) :
                objectCopy[key].replace("%+t%", "");
            } else {
              objectCopy[baseKey] = objectCopy[key]
            }

          }
          delete objectCopy[key];
        }
      }
      return objectCopy;
    }
    return obj
  }

  parseHistoryValue(history: TaskHistory, type: 'old' | 'new') {
    let value = type == 'old' ? history.oldValue : history.newValue;
    let field = history.field;
    let cfg = structuredClone(this.historyFields.find(c => c.field == field));

    if (cfg) {
      if (cfg.nestedField) {
        cfg.nestedField = type + "ValueDetails." + cfg.nestedField
      } else {
        cfg.field = type + "Value"
      }
      return this.getCellData(cfg, history)
    }

    return value
  }

  getActionData(actionId: string | number | null) {
    return this.actions.find(a => a.actionId == actionId)
  }

  openLink(link?: string) {
    if (!link) return;
    window.open(link, '_blank');
  }
}
